// src/components/RecheckPanel.jsx
// 授業の途中・最後の「もう一度確認」【2026-09-28】
//
//   最初に打刻だけして抜ける学生を見つけるため、同じ回の中でもう一度打刻させる。
//   最初の受付を終了してから開き直して取る。確認のあいだは、最初に出席した学生の
//   2回目だけを受け取り、新しい出席は取らない（受付中に開くとサーバーが断る）。
//   ビーコンでもコードでも（両方でも）よく、最初の打刻と違う方法でもよい。
//   回は増やさないので出席率は変わらない。応えなかった学生は出欠を変えず、
//   ここに名前を出してお知らせにも残すだけ（直すときは教員が手で直す）。
//
//   サーバー: /api/attendance/sessions/:id/recheck（recheckController）
//   コードは /api/attendance/otp/generate に sessionId を付けて発行する。
import React, { useCallback, useEffect, useState } from 'react';

const authHeader = () => ({
  Authorization: 'Bearer ' + localStorage.getItem('token'),
  'Content-Type': 'application/json',
});

/**
 * @param {object} props
 * @param {{sessionId:number, classId:number, className:string}} props.session
 * @param {boolean} props.beaconMissing  教員にビーコンが無い
 * @param {number}  props.otpSec         コードの有効時間（秒）
 * @param {boolean} [props.startOpen]    最初から方法の選択を開いておく
 * @param {boolean} [props.sessionActive] 最初の受付がまだ開いている（確認は開けない）
 * @param {Function} [props.onChanged]   受付やコードが変わったとき（一覧の読み直し）
 * @param {Function} [props.onDone]      閉じるとき
 * @param {Function} [props.onClosed]    確認を締めたとき（結果を渡す。親で表示を残す）
 */
export default function RecheckPanel({ session, beaconMissing, otpSec, startOpen, sessionActive, onChanged, onDone, onClosed }) {
  const [info, setInfo]       = useState(null);    // サーバーの状況
  const [choosing, setChoosing] = useState(!!startOpen);
  const [useBeacon, setUseBeacon] = useState(!beaconMissing);
  const [useCode, setUseCode] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);
  const [showMissing, setShowMissing] = useState(false);

  const sid = session.sessionId;

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/attendance/sessions/' + sid + '/recheck', { headers: authHeader() });
      const d = await r.json();
      if (d && d.success) setInfo(d.available === false ? { unavailable: true } : (d.recheck || null));
    } catch (e) { /* 取れなくても操作はできる */ }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  // 確認のあいだは人数を追いかける
  const isOpen = !!(info && info.isOpen);
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [isOpen, load]);

  const start = async () => {
    if (!useBeacon && !useCode) { setMsg({ text: 'ビーコンかコードのどちらかを選んでください', isError: true }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/attendance/sessions/' + sid + '/recheck', {
        method: 'POST', headers: authHeader(), body: JSON.stringify({ beacon: useBeacon }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setMsg({ text: d.message || '確認を開けませんでした', isError: true }); return; }
      const notes = [];
      if (useBeacon && d.beaconNote) notes.push(d.beaconNote);
      if (useCode) {
        const r2 = await fetch('/api/attendance/otp/generate', {
          method: 'POST', headers: authHeader(),
          body: JSON.stringify({ classId: session.classId, sessionId: sid, expiresInSeconds: otpSec }),
        });
        const d2 = await r2.json();
        if (!d2.success) notes.push('コードを発行できませんでした: ' + (d2.message || ''));
      }
      setChoosing(false);
      setMsg({ text: d.message + (notes.length ? '（' + notes.join(' / ') + '）' : ''), isError: notes.length > 0 });
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setMsg({ text: '通信エラー: ' + e.message, isError: true });
    } finally { setBusy(false); }
  };

  const close = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await fetch('/api/attendance/sessions/' + sid + '/recheck/close', {
        method: 'POST', headers: authHeader(),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setMsg({ text: d.message || '締められませんでした', isError: true }); return; }
      setMsg({ text: d.message, isError: d.missing && d.missing.length > 0 });
      setShowMissing(true);
      if (onClosed) onClosed(d);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setMsg({ text: '通信エラー: ' + e.message, isError: true });
    } finally { setBusy(false); }
  };

  if (info && info.unavailable) {
    return (
      <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.3rem' }}>
        「もう一度確認」はデータベースの更新（v46）を当てると使えます
      </div>
    );
  }

  // 最初の受付がまだ開いている回：確認は開けないので、前回の結果と案内だけ出す
  if (sessionActive && !isOpen && !choosing) {
    if (!info) return null;   // まだ確認を取っていない回には何も足さない
  }

  const box = {
    width: '100%', border: '1px dashed #a78bfa', background: '#faf5ff', borderRadius: 8,
    padding: '0.55rem 0.7rem', marginTop: '0.45rem', fontSize: '0.88rem',
  };

  return (
    <div style={box}>
      {isOpen ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200 }}>
            <strong style={{ color: '#6d28d9' }}>{info.round}回目の確認中</strong>
            <span style={{ marginLeft: '0.6rem' }}>
              応えた <strong>{info.marked}</strong> / {info.attended} 人
            </span>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
              最初に出席した学生の2回目だけを受け付けています。新しい出席は取りません
            </div>
          </span>
          <button className="btn-primary" disabled={busy} onClick={close} style={{ minHeight: '2.2rem' }}>
            確認を締める
          </button>
        </div>
      ) : choosing ? (
        <div>
          <div style={{ fontWeight: 700, color: '#6d28d9', marginBottom: '0.35rem' }}>
            もう一度確認（{session.className}）
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem',
                            color: beaconMissing ? '#9ca3af' : undefined }}>
              <input type="checkbox" checked={useBeacon && !beaconMissing} disabled={beaconMissing}
                     onChange={e => setUseBeacon(e.target.checked)} />
              ビーコン
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input type="checkbox" checked={useCode} onChange={e => setUseCode(e.target.checked)} />
              6桁コード
            </label>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.45rem' }}>
            最初の打刻と違う方法でもかまいません。応えなかった学生は出欠を変えず、名前を出して知らせるだけです。
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-primary" disabled={busy} onClick={start}>確認を始める</button>
            <button className="btn-secondary" disabled={busy}
                    onClick={() => { setChoosing(false); if (onDone) onDone(); }}>やめる</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200, color: '#4b5563' }}>
            {info
              ? <>前回（{info.round}回目）の確認：応えた {info.marked} / {info.attended} 人
                  {info.missing && info.missing.length > 0 && (
                    <button type="button" className="btn-link" onClick={() => setShowMissing(v => !v)}
                            style={{ marginLeft: '0.5rem', background: 'none', border: 'none',
                                     color: '#b91c1c', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                      応えていない {info.missing.length}人
                    </button>
                  )}</>
              : '最初に出席した学生だけに、もう一度打刻してもらいます（新しい出席は取りません）'}
          </span>
          {!sessionActive && (
            <button className="btn-secondary" disabled={busy} onClick={() => setChoosing(true)}
                    style={{ minHeight: '2.2rem' }}>
              もう一度確認…
            </button>
          )}
        </div>
      )}

      {showMissing && info && info.missing && info.missing.length > 0 && (
        <div style={{ marginTop: '0.45rem', background: '#fff', border: '1px solid #fecaca',
                      borderRadius: 6, padding: '0.45rem 0.6rem', fontSize: '0.85rem' }}>
          <div style={{ color: '#b91c1c', fontWeight: 700, marginBottom: '0.2rem' }}>
            {info.round}回目の確認に応えていない学生（{info.missing.length}人）
          </div>
          {info.missing.map(m => (
            <div key={m.userId}>{m.studentId || ''}　{m.name || ''}
              <span style={{ color: '#9ca3af', marginLeft: '0.4rem' }}>
                {m.status === 'late' ? '遅刻' : m.status === 'early_leave' ? '早退' : m.status === 'irregular' ? '要確認' : '出席'}
              </span>
            </div>
          ))}
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.25rem' }}>
            出欠は変えていません。欠席などに変えるときは、出席記録から手で直してください。
          </div>
        </div>
      )}

      {msg && (
        <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: msg.isError ? '#b91c1c' : '#166534' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}
